/**
 * Флаги стран — свои, инлайновые.
 *
 * Эмодзи-флаги в интерфейсе запрещены: это чужая палитра, и на Windows их
 * вовсе нет. Рисунки упрощены до того, что читается в 14 пикселей: полосы,
 * полумесяц, солнце. Детали вроде герба или орнамента на таком размере
 * превращаются в грязь, поэтому их нет.
 *
 * Соотношение сторон у всех одно (10:7) — иначе чипы в ряду «прыгают».
 */
import type { CSSProperties, ReactNode } from 'react';

const W = 20;
const H = 14;

/** Полосы сверху вниз. */
function Stripes(colors: string[]): ReactNode {
  const h = H / colors.length;
  return (
    <>
      {colors.map((c, i) => (
        <rect key={c + i} x="0" y={i * h} width={W} height={h} fill={c} />
      ))}
    </>
  );
}

/** Вертикальные полосы слева направо. */
function Vertical(colors: string[]): ReactNode {
  const w = W / colors.length;
  return (
    <>
      {colors.map((c, i) => (
        <rect key={c + i} x={i * w} y="0" width={w} height={H} fill={c} />
      ))}
    </>
  );
}

/** Полумесяц со звездой — вырезанием круга, а не дугой: так он чище в 14px. */
function CrescentStar(cx: number, cy: number, r: number, color: string, id: string): ReactNode {
  return (
    <>
      <mask id={id}>
        <rect x="0" y="0" width={W} height={H} fill="#fff" />
        <circle cx={cx + r * 0.42} cy={cy} r={r * 0.82} fill="#000" />
      </mask>
      <circle cx={cx} cy={cy} r={r} fill={color} mask={`url(#${id})`} />
      <path
        d={star(cx + r * 1.65, cy, r * 0.62)}
        fill={color}
      />
    </>
  );
}

/** Пятиконечная звезда точками — у path предсказуемый размер. */
function star(cx: number, cy: number, r: number): string {
  const pts: string[] = [];
  for (let i = 0; i < 10; i++) {
    const rr = i % 2 === 0 ? r : r * 0.42;
    const a = (Math.PI / 5) * i - Math.PI / 2;
    pts.push(`${(cx + rr * Math.cos(a)).toFixed(2)},${(cy + rr * Math.sin(a)).toFixed(2)}`);
  }
  return `M${pts.join('L')}Z`;
}

/** Солнце с лучами — Кыргызстан и Казахстан. */
function Sun(cx: number, cy: number, r: number, color: string, rays: number): ReactNode {
  const lines: ReactNode[] = [];
  for (let i = 0; i < rays; i++) {
    const a = (2 * Math.PI * i) / rays;
    lines.push(
      <line
        key={i}
        x1={cx + Math.cos(a) * (r + 0.7)}
        y1={cy + Math.sin(a) * (r + 0.7)}
        x2={cx + Math.cos(a) * (r + 2.1)}
        y2={cy + Math.sin(a) * (r + 2.1)}
        stroke={color}
        strokeWidth="0.55"
        strokeLinecap="round"
      />,
    );
  }
  return (
    <>
      {lines}
      <circle cx={cx} cy={cy} r={r} fill={color} />
    </>
  );
}

const FLAGS: Record<string, ReactNode> = {
  TJ: (
    <>
      {Stripes(['#cc0000', '#ffffff', '#006600'])}
      <circle cx="10" cy="7" r="1.9" fill="none" stroke="#f8c300" strokeWidth="0.7" />
      <path d={star(10, 3.9, 0.8)} fill="#f8c300" />
    </>
  ),
  UZ: (
    <>
      {Stripes(['#0099b5', '#ffffff', '#1eb53a'])}
      <rect x="0" y="4.4" width={W} height="0.35" fill="#ce1126" />
      <rect x="0" y="9.25" width={W} height="0.35" fill="#ce1126" />
      {CrescentStar(4.6, 2.3, 1.35, '#ffffff', 'fl-uz')}
    </>
  ),
  KZ: (
    <>
      <rect x="0" y="0" width={W} height={H} fill="#00afca" />
      {Sun(10, 6.2, 1.9, '#fec50c', 12)}
      <path d="M6.2 10.4c1.3-1 2.3-1 3.8 0 1.5 1 2.5 1 3.8 0" stroke="#fec50c" strokeWidth="0.6" fill="none" strokeLinecap="round" />
    </>
  ),
  KG: (
    <>
      <rect x="0" y="0" width={W} height={H} fill="#e8112d" />
      {Sun(10, 7, 2.1, '#ffef00', 14)}
      <circle cx="10" cy="7" r="1.2" fill="none" stroke="#e8112d" strokeWidth="0.45" />
    </>
  ),
  TM: (
    <>
      <rect x="0" y="0" width={W} height={H} fill="#00843d" />
      <rect x="2.6" y="0" width="3.2" height={H} fill="#b40000" />
      {[2.2, 5.2, 8.2, 11.2].map((y) => (
        <rect key={y} x="3.5" y={y} width="1.4" height="1.4" rx="0.3" fill="#ffffff" fillOpacity="0.85" />
      ))}
      {CrescentStar(11.6, 4.6, 1.5, '#ffffff', 'fl-tm')}
    </>
  ),
  AZ: (
    <>
      {Stripes(['#0092bc', '#e8112d', '#00ae65'])}
      {CrescentStar(8.4, 7, 1.7, '#ffffff', 'fl-az')}
    </>
  ),
  GE: (
    <>
      <rect x="0" y="0" width={W} height={H} fill="#ffffff" />
      <rect x="8.6" y="0" width="2.8" height={H} fill="#ff0000" />
      <rect x="0" y="5.6" width={W} height="2.8" fill="#ff0000" />
      {[
        [4.2, 2.8],
        [15.8, 2.8],
        [4.2, 11.2],
        [15.8, 11.2],
      ].map(([x, y]) => (
        <g key={`${x}-${y}`} fill="#ff0000">
          <rect x={(x as number) - 0.45} y={(y as number) - 1.5} width="0.9" height="3" rx="0.2" />
          <rect x={(x as number) - 1.5} y={(y as number) - 0.45} width="3" height="0.9" rx="0.2" />
        </g>
      ))}
    </>
  ),
  AM: <>{Stripes(['#d90012', '#0033a0', '#f2a800'])}</>,
  TR: (
    <>
      <rect x="0" y="0" width={W} height={H} fill="#e30a17" />
      {CrescentStar(7.8, 7, 2.1, '#ffffff', 'fl-tr')}
    </>
  ),
  MN: (
    <>
      {Vertical(['#c4272f', '#015197', '#c4272f'])}
      <g fill="#f9cf02">
        <path d="M2.6 3.1h1.4v1.1H2.6z" />
        <path d="M3.3 4.6l0.9 1.5H2.4z" />
        <circle cx="3.3" cy="7.4" r="0.85" />
        <path d="M2.4 8.9h1.8v0.7H2.4z" />
        <path d="M2.4 10.2h1.8v0.7H2.4z" />
      </g>
    </>
  ),
  /* ── Дальние рынки ──
     Рисунки упрощены сильнее обычного: в 14 пикселей от звёзд на флаге США
     остаются точки, а от герба — пятно, и честнее показать узнаваемую схему,
     чем грязь. */
  US: (
    <>
      {Stripes(['#b22234', '#ffffff', '#b22234', '#ffffff', '#b22234', '#ffffff', '#b22234'])}
      <rect x="0" y="0" width="9" height="7.5" fill="#3c3b6e" />
      {[1.6, 3.6, 5.6, 7.6].map((x) =>
        [1.5, 3.3, 5.1].map((y) => (
          <circle key={`${x}-${y}`} cx={x} cy={y} r="0.42" fill="#ffffff" />
        )),
      )}
    </>
  ),
  GB: (
    <>
      <rect x="0" y="0" width={W} height={H} fill="#012169" />
      <path d="M0 0 20 14M20 0 0 14" stroke="#ffffff" strokeWidth="2.6" />
      <path d="M0 0 20 14M20 0 0 14" stroke="#c8102e" strokeWidth="1.1" />
      <path d="M10 0v14M0 7h20" stroke="#ffffff" strokeWidth="4" />
      <path d="M10 0v14M0 7h20" stroke="#c8102e" strokeWidth="2.2" />
    </>
  ),
  DE: <>{Stripes(['#000000', '#dd0000', '#ffce00'])}</>,
  FR: <>{Vertical(['#002395', '#ffffff', '#ed2939'])}</>,
  ES: (
    <>
      <rect x="0" y="0" width={W} height={H} fill="#c60b1e" />
      <rect x="0" y="3.5" width={W} height="7" fill="#ffc400" />
    </>
  ),
  JP: (
    <>
      <rect x="0" y="0" width={W} height={H} fill="#ffffff" />
      <circle cx="10" cy="7" r="3.4" fill="#bc002d" />
    </>
  ),
  KR: (
    <>
      <rect x="0" y="0" width={W} height={H} fill="#ffffff" />
      <path d="M6.6 7a3.4 3.4 0 0 1 6.8 0 1.7 1.7 0 0 0-3.4 0 1.7 1.7 0 0 1-3.4 0Z" fill="#cd2e3a" />
      <path d="M6.6 7a3.4 3.4 0 0 0 6.8 0 1.7 1.7 0 0 1-3.4 0 1.7 1.7 0 0 0-3.4 0Z" fill="#0047a0" />
      {[
        [2.6, 3.2],
        [2.6, 10.8],
        [17.4, 3.2],
        [17.4, 10.8],
      ].map(([x, y]) => (
        <rect key={`${x}-${y}`} x={(x as number) - 1.3} y={(y as number) - 0.3} width="2.6" height="0.6" fill="#000000" />
      ))}
    </>
  ),
  SG: (
    <>
      <rect x="0" y="0" width={W} height="7" fill="#ed2939" />
      <rect x="0" y="7" width={W} height="7" fill="#ffffff" />
      {CrescentStar(4.4, 3.5, 1.7, '#ffffff', 'fl-sg')}
    </>
  ),
  AE: (
    <>
      {Stripes(['#00732f', '#ffffff', '#000000'])}
      <rect x="0" y="0" width="5" height={H} fill="#ff0000" />
    </>
  ),
  SA: (
    <>
      <rect x="0" y="0" width={W} height={H} fill="#006c35" />
      <rect x="3.6" y="5" width="12.8" height="0.9" rx="0.3" fill="#ffffff" />
      <rect x="3.6" y="8.4" width="10" height="0.7" rx="0.3" fill="#ffffff" />
      <path d="M14 8.75h2.6" stroke="#ffffff" strokeWidth="0.7" strokeLinecap="round" />
    </>
  ),
  QA: (
    <>
      <rect x="0" y="0" width={W} height={H} fill="#8d1b3d" />
      <path d="M0 0h6l2.4 1.75L6 3.5l2.4 1.75L6 7l2.4 1.75L6 10.5l2.4 1.75L6 14H0z" fill="#ffffff" />
    </>
  ),
  EG: (
    <>
      {Stripes(['#ce1126', '#ffffff', '#000000'])}
      <circle cx="10" cy="7" r="1.25" fill="#c09300" />
    </>
  ),
};

/**
 * Флаг страны по коду ISO. Неизвестный код — ничего: лучше пустое место, чем
 * чужой флаг рядом с названием.
 */
export function Flag({
  code,
  size = 14,
  className,
  style,
}: {
  code: string;
  size?: number;
  className?: string;
  style?: CSSProperties;
}) {
  const art = FLAGS[code.toUpperCase()];
  if (!art) return null;

  return (
    <svg
      width={(size * W) / H}
      height={size}
      viewBox={`0 0 ${W} ${H}`}
      className={className}
      style={{ borderRadius: 2.5, flex: 'none', display: 'block', ...style }}
      aria-hidden
    >
      {art}
      {/* Волосок по контуру: белые флаги иначе сливаются со светлой темой. */}
      <rect
        x="0.25"
        y="0.25"
        width={W - 0.5}
        height={H - 0.5}
        rx="2"
        fill="none"
        stroke="rgba(0,0,0,0.22)"
        strokeWidth="0.5"
      />
    </svg>
  );
}

/** Есть ли у страны флаг — чтобы не оставлять в вёрстке пустой отступ. */
export function hasFlag(code: string): boolean {
  return Boolean(FLAGS[code.toUpperCase()]);
}
